import { z } from 'zod';
import { dataSchema, situacaoDocumentoSchema, uuidSchema } from './comum.ts';
import { situacaoCotacaoSchema } from './cotacoes-preco.ts';
import { situacaoSolicitacaoSchema } from './solicitacoes-compra.ts';

/**
 * Painel de compras — os números com que o comprador e a gerência abrem o dia
 * (`.spec/010`, módulo Compras).
 *
 * É **só leitura e só agregação**: nenhum documento nasce, muda ou some por
 * aqui. O painel conta o que os três módulos de compra já gravaram — solicitação,
 * cotação e pedido — e não recalcula nada. As situações que ele agrupa são
 * derivadas **e gravadas** (ver `situacaoSolicitacaoSchema`), então contar por
 * elas é ler uma coluna indexada, e não varrer os filhos de cada documento.
 *
 * ## Dois relógios, de propósito
 *
 * O painel responde a duas perguntas que **não** têm a mesma janela, e misturá-las
 * seria o defeito silencioso deste tipo de tela:
 *
 * - **Os indicadores são a fila de hoje, sem recorte de data.** A solicitação de
 *   abril ainda pendente em agosto é justamente a que precisa aparecer — é a que
 *   ninguém tocou. Limitá-la aos últimos 30 dias faria a fila encolher sozinha
 *   com o tempo, e o número mais tranquilizador seria o do mês mais esquecido.
 * - **Os gráficos são o movimento dos últimos 30 dias.** Ali a pergunta é outra —
 *   "o que se lançou e em que pé ficou" —, e sem recorte de período o gráfico
 *   viraria o histórico inteiro da conta, em que o ano passado esmaga o mês atual.
 *
 * Por isso `de` e `ate` viajam na resposta: a legenda da tela precisa dizer de
 * que período o gráfico fala, e o cliente não tem como recalcular a janela sem
 * repetir a regra do fuso.
 */

/**
 * O tamanho da janela dos gráficos, em dias, incluindo hoje.
 *
 * Constante, e não parâmetro: um seletor de período aqui transformaria o painel
 * em relatório, e relatório com filtro de data já existe — é a listagem de cada
 * documento, que tem `de` e `ate`. O painel é a tela de relance.
 */
export const JANELA_PAINEL_COMPRAS_DIAS = 30;

export interface JanelaDoPainelDeCompras {
  /** Primeiro dia contado, em `AAAA-MM-DD`. */
  de: string;
  /** Último dia contado — hoje. */
  ate: string;
}

/** `AAAA-MM-DD` a partir dos componentes **locais** da data. Ver `janelaDoPainelDeCompras`. */
function iso(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');

  return `${data.getFullYear()}-${mes}-${dia}`;
}

/**
 * A janela dos gráficos, a partir do instante em que se pergunta.
 *
 * **Recebe o instante em vez de chamar `new Date()` por dentro**: é o que a torna
 * testável sem congelar o relógio, e o que permite ao teste conferir a virada de
 * mês e a de ano, que são onde a aritmética de dia erra.
 *
 * Os componentes são os **locais** (`getDate`), nunca `toISOString()`: as datas
 * do sistema são de negócio — o documento é do dia 10, não das 14h37 do dia 10 em
 * São Paulo —, e o UTC faria a janela aberta às 22h já contar o dia seguinte,
 * deixando o painel exibir um período que termina amanhã.
 *
 * A janela **inclui hoje**, e por isso volta 29 dias e não 30: "últimos 30 dias"
 * com hoje de fora seria um período que não contém o pedido lançado há uma hora —
 * exatamente o que quem abre o painel acabou de fazer.
 */
export function janelaDoPainelDeCompras(agora: Date): JanelaDoPainelDeCompras {
  return janelaDeUltimosDias(agora, JANELA_PAINEL_COMPRAS_DIAS);
}

/**
 * A mesma janela, com o tamanho como parâmetro — o que o painel de contas a
 * pagar reusa para os gráficos de contas pagas.
 *
 * Existe porque a alternativa era uma segunda cópia da aritmética acima em outro
 * módulo, e o que ela tem de delicado — o dia local e o "volta `dias − 1`" — é
 * exatamente o que uma cópia erra. Os dois painéis usam 30 dias hoje, e o
 * parâmetro é o que permite a um deles mudar sem arrastar o outro.
 *
 * O tamanho continua **não** sendo um parâmetro de rota: quem o escolhe é o
 * painel, no servidor. Ver `JANELA_PAINEL_COMPRAS_DIAS`.
 */
export function janelaDeUltimosDias(agora: Date, dias: number): JanelaDoPainelDeCompras {
  const ate = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());

  const de = new Date(ate);
  de.setDate(de.getDate() - (dias - 1));

  return { de: iso(de), ate: iso(ate) };
}

/**
 * O recorte do painel.
 *
 * Os dois filtros são opcionais, e ausentes significam "tudo que o usuário
 * alcança" — o recorte por empresa vinculada continua sendo do servidor, como em
 * toda listagem.
 *
 * **A obra alcança os três documentos por caminhos diferentes**, e isso não é
 * inconsistência: é o modelo. A solicitação tem obra no cabeçalho; o pedido
 * abastece várias no mesmo carregamento e só o rateio dos itens sabe quais; a
 * cotação não tem obra nenhuma — o que ela tem é a procedência, o item de
 * solicitação de onde nasceu. A consequência tem de ser dita na tela: com uma
 * obra escolhida, a **cotação avulsa fica de fora**, porque não há de onde tirar
 * a obra dela. Somá-la a todas as obras seria pior — o mesmo número apareceria
 * repetido em cada recorte.
 */
// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const painelDeComprasQuerySchema = z
  .object({
    empresaId: uuidSchema.optional(),
    obraId: uuidSchema.optional(),
  })
  .strict();

export type PainelDeComprasQuery = z.infer<typeof painelDeComprasQuerySchema>;

const quantidadeDaFatiaSchema = z.number().int().nonnegative();

export const fatiaDeSolicitacoesSchema = z.object({
  situacao: situacaoSolicitacaoSchema,
  quantidade: quantidadeDaFatiaSchema,
});

export const fatiaDeCotacoesSchema = z.object({
  situacao: situacaoCotacaoSchema,
  quantidade: quantidadeDaFatiaSchema,
});

export const fatiaDePedidosSchema = z.object({
  situacao: situacaoDocumentoSchema,
  quantidade: quantidadeDaFatiaSchema,
});

export type FatiaDeSolicitacoes = z.infer<typeof fatiaDeSolicitacoesSchema>;
export type FatiaDeCotacoes = z.infer<typeof fatiaDeCotacoesSchema>;
export type FatiaDePedidos = z.infer<typeof fatiaDePedidosSchema>;

/**
 * A fila de hoje, sem recorte de data.
 *
 * Quatro números, e cada um é uma tela: clicar em "pendentes" leva à listagem de
 * solicitações já filtrada em `PENDENTE`. É o que impede o painel de ser um lugar
 * onde se lê o problema e não se chega a ele.
 *
 * **"Em cotação" é uma situação, não uma etapa inventada aqui**: a solicitação
 * passa a `COTACAO` quando ao menos um item dela vira cotação de preço, e quem a
 * escreve é `recalcularSituacaoDasSolicitacoes`. O painel apenas conta.
 */
export const indicadoresDoPainelSchema = z.object({
  solicitacoesPendentes: quantidadeDaFatiaSchema,
  solicitacoesEmCotacao: quantidadeDaFatiaSchema,
  /**
   * Cotações `ABERTA` **e vigentes**.
   *
   * A desativada fica de fora, e é decisão: `ativo` é a vigência do preço, e a
   * cotação de março com preço vencido não é trabalho esperando resposta — é
   * histórico. Contá-la faria a fila crescer para sempre, já que a cotação
   * vencida que ninguém encerrou continuaria `ABERTA` indefinidamente.
   */
  cotacoesEmAberto: quantidadeDaFatiaSchema,
  /** Pedidos `PENDENTE`: sem entrega, ou com entrega parcial. */
  pedidosEmAberto: quantidadeDaFatiaSchema,
});

export type IndicadoresDoPainel = z.infer<typeof indicadoresDoPainelSchema>;

export const painelDeComprasSchema = z.object({
  de: dataSchema,
  ate: dataSchema,
  indicadores: indicadoresDoPainelSchema,
  /** Pela data de lançamento da solicitação. */
  solicitacoesPorSituacao: z.array(fatiaDeSolicitacoesSchema),
  /** Pela data da cotação — o dia em que se perguntou. */
  cotacoesPorSituacao: z.array(fatiaDeCotacoesSchema),
  /** Pela data de lançamento do pedido. */
  pedidosPorSituacao: z.array(fatiaDePedidosSchema),
});

export type PainelDeCompras = z.infer<typeof painelDeComprasSchema>;

/**
 * A série completa de um gráfico: **todas** as situações, na ordem do enum,
 * inclusive as que não aconteceram no período.
 *
 * Sem isto o `GROUP BY` devolveria só as situações com linha, e a barra ausente
 * se lê como "esta situação não existe" em vez de "nenhuma neste período" — que
 * é justamente a informação boa quando o que falta é "cancelado".
 *
 * A ordem é a do enum, e ela tem significado: é a ordem do processo (pendente →
 * cotação → atendido → encerrado). Ordenar por quantidade faria as barras
 * trocarem de lugar a cada abertura da tela, e o olho perderia a comparação com
 * a leitura de ontem.
 *
 * Vive no contrato, e não no service, porque é o formato que a tela lê: se a API
 * um dia deixasse de completar a série, o gráfico mudaria de forma sem que nada
 * falhasse.
 */
export function serieDeSituacoes<T extends string>(
  situacoes: readonly T[],
  contagens: Iterable<readonly [string, number]>,
): { situacao: T; quantidade: number }[] {
  const mapa = new Map(contagens);

  return situacoes.map((situacao) => ({ situacao, quantidade: mapa.get(situacao) ?? 0 }));
}

/**
 * O total de uma série.
 *
 * É o "de N documentos" da legenda do gráfico. Existe aqui, e não somado na
 * tela, porque a tela soma três séries e a terceira cópia é a que esquece a
 * fatia zerada.
 */
export function totalDaSerie(fatias: readonly { quantidade: number }[]): number {
  return fatias.reduce((soma, fatia) => soma + fatia.quantidade, 0);
}
